import { Component, useMemo, useEffect } from 'react';
import { ReactReduxContext, connect, useDispatch, useStore } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { createDisplayName } from '@dbh/react-extra';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { jsx } from 'react/jsx-runtime';
import 'prop-types';
import '@dbh/generic-types';
import '@dbh/redux-types';
import { useThrowIfPreviousValueDeepButNotReferenceEqual } from '@dbh/react-hooks';
import 'invariant';

var injectReducer = withConformsTo("injectReducer",[],(/**
     * `key`: A key of the reducer.
     * `reducer`: A reducer that will be injected.
     * @see {@link https://github.com/jsdoc/jsdoc/issues/1286#issuecomment-308899921}
      @type {{key: string, reducer: Function}} */a)=>{let{key:b,reducer:c}=a;return (/** @type {React.Component} */a)=>{class d extends Component{constructor(a,d){super(a);const{store:e}=d;e.createReducer(b,c);}render(){return/*#__PURE__*/jsx(a,{...this.props})}}d.WrappedComponent=a,d.displayName=createDisplayName("withReducer",a),d.contextType=ReactReduxContext;// We need to keep this as a class component, so that it can also inject
// reducers in server side rendering.
// eslint-disable-next-line @dbh/redux-extra/prefer-hooks-to-connect
const e=connect(null)(d);return hoistNonReactStatics(e,a)}});

/*
 *
 * Constants: `@dbh/redux-extra`.
 *
 */// @see {@link https://github.com/react-boilerplate/redux-injectors/blob/master/docs/api.md#sagainjectionmodes}
const RESTART_ON_REMOUNT="@@saga-injector/restart-on-remount";const DAEMON="@@saga-injector/daemon";const ONCE_TILL_UNMOUNT="@@saga-injector/once-till-unmount";const SAGA_INJECTED="@@saga-injector/SAGA_INJECTED";const SAGA_EJECTED="@@saga-injector/SAGA_EJECTED";const REDUCER_INJECTED="@@reducer-injector/REDUCER_INJECTED";const REDUCER_EJECTED="@@reducer-injector/REDUCER_EJECTED";

/**
 * Returns saga injector.
 * @param {Object} store .
 * @return {Function} .
 */const injectSagaFactory=withConformsTo("injectSagaFactory",[],a=>withConformsTo("injectSagaFactory",[],(b,c,d)=>{const e={...c,mode:c.mode||RESTART_ON_REMOUNT},{saga:f,mode:g}=e;let h=Reflect.has(a.injectedSagas,b);return (!h||h&&g!==DAEMON&&g!==ONCE_TILL_UNMOUNT)&&(a.injectedSagas[b]={...e,task:a.runSaga(f,d)}),{wasAlreadyInjected:h}}));/**
 * Returns eject saga function.
 * @param {Object} store .
 * @return {Function} .
 */const ejectSagaFactory=withConformsTo("ejectSagaFactory",[],a=>withConformsTo("ejectSagaFactory",[],b=>{if(Reflect.has(a.injectedSagas,b)){const d=a.injectedSagas[b];if(d.mode&&d.mode!==DAEMON){var c;// During jest tests, the `task` property is `undefined`. Probably
// because the task is not started/running.
null==(c=d.task)||c.cancel(),a.injectedSagas[b]="done";}}}));const getInjectors=withConformsTo("getInjectors",[],a=>({injectSaga:injectSagaFactory(a),ejectSaga:ejectSagaFactory(a)}));var getSagaInjectors = getInjectors;

const useBoundEventCreator=withConformsTo("useBoundEventCreator",[],a=>{// eslint-disable-next-line @dbh/redux-extra/use-create-use-bound-event-creator
const b=useDispatch(),c=useMemo(()=>function(){return b(a(...arguments))},[b,a]);return c}),createUseBoundEventCreator=withConformsTo("createUseBoundEventCreator",[],a=>()=>useBoundEventCreator(a));var createUseBoundEventCreator$1 = createUseBoundEventCreator;

/**
 * A saga has been injected with `injectSaga`.
 * @param {string} key The `REDUX_AND_SAGA_KEY` of the saga.
 * @param {Object} obj .
 * @param {boolean} obj.wasAlreadyInjected .
 * @return {Object} .
 */const sagaInjected=withConformsTo("sagaInjected",[],(a,b)=>{let{wasAlreadyInjected:c}=b;return {type:SAGA_INJECTED,payload:{key:a,wasAlreadyInjected:c}}});const useSagaInjected=createUseBoundEventCreator$1(sagaInjected);/**
 * A saga previously injected with `injectSaga` has been "ejected".
 * @param {string} key The `REDUX_AND_SAGA_KEY` of the saga.
 * @return {Object} .
 */const sagaEjected=withConformsTo("sagaEjected",[],a=>({type:SAGA_EJECTED,payload:{key:a}}));const useSagaEjected=createUseBoundEventCreator$1(sagaEjected);/**
 * A reducer has been injected with `injectReducer`.
 * @param {string} key The `REDUX_AND_SAGA_KEY` of the reducer.
 * @return {Object} .
 */const reducerInjected=withConformsTo("reducerInjected",[],(a,b)=>({type:REDUCER_INJECTED,payload:{key:a,initialState:b}}));

var injectSaga = withConformsTo("injectSaga",[],(/**
     * `key`: A key of the saga.
     * `saga`: A root saga that will be injected.
     * `mode`: By default `(constants.RESTART_ON_REMOUNT)`.
     * The saga will be started on component mount and
     * cancelled with `task.cancel()` on component un-mount for improved performance.
     * Another two options:
     * - `constants.DAEMON` starts the saga on component mount and
     * never cancels it or starts again,
     * - `constants.ONCE_TILL_UNMOUNT` behaves like 'RESTART_ON_REMOUNT'
     * but never runs it again.
     * @see {@link https://github.com/jsdoc/jsdoc/issues/1286#issuecomment-308899921}
     * @type {{key: string, saga: Function, mode: string?}}
     */a)=>(/** @type {React.Component} */b)=>{const{key:c,saga:d,mode:e}=a;class f extends Component{constructor(a,b){super(a);const{store:f}=b;this.injectors=getSagaInjectors(f);const{injectSaga:g}=this.injectors,{wasAlreadyInjected:h}=g(c,{saga:d,mode:e},this.props);sagaInjected(c,{wasAlreadyInjected:h});}componentWillUnmount(){const{ejectSaga:a}=this.injectors;a(c),sagaEjected(c);}render(){return/*#__PURE__*/jsx(b,{...this.props})}}f.WrappedComponent=b,f.displayName=createDisplayName("withSaga",b),f.contextType=ReactReduxContext;// We need to keep this as a class component, so that it can also inject
// reducers in server side rendering.
// eslint-disable-next-line @dbh/redux-extra/prefer-hooks-to-connect
const g=connect(null)(f);return hoistNonReactStatics(g,b)});

var injectSagaAndReducer = withConformsTo("injectSagaAndReducer",[],(/**
     * `key`: A key of the saga.
     * `saga`: A root saga that will be injected.
     * `mode`: By default `(constants.RESTART_ON_REMOUNT)`.
     * The saga will be started on component mount and
     * cancelled with `task.cancel()` on component un-mount for improved performance.
     * Another two options:
     * - `constants.DAEMON` starts the saga on component mount and
     * never cancels it or starts again,
     * - `constants.ONCE_TILL_UNMOUNT` behaves like 'RESTART_ON_REMOUNT'
     * but never runs it again.
     * @see {@link https://github.com/jsdoc/jsdoc/issues/1286#issuecomment-308899921}
     * @type {{key: string, saga: Function, mode: string?}}
     */a,/**
     * `key`: A key of the reducer.
     * `reducer`: A reducer that will be injected.
     * @see {@link https://github.com/jsdoc/jsdoc/issues/1286#issuecomment-308899921}
      @type {{key: string, reducer: Function}} */b)=>(/** @type {React.Component} */c)=>{const{key:d,saga:e,mode:f}=a,{key:g,reducer:h}=b;class i extends Component{constructor(a,b){super(a);const{store:c}=b;this.injectors=getSagaInjectors(c);const{injectSaga:i}=this.injectors;c.createReducer(g,h);const{wasAlreadyInjected:j}=i(d,{saga:e,mode:f},this.props);sagaInjected(d,{wasAlreadyInjected:j});}componentWillUnmount(){const{ejectSaga:a}=this.injectors;a(d),sagaEjected(d);}render(){return/*#__PURE__*/jsx(c,{...this.props})}}i.WrappedComponent=c,i.displayName=createDisplayName("withSagaAndReducer",c),i.contextType=ReactReduxContext;// We need to keep this as a class component, so that it can also inject
// reducers in server side rendering.
// eslint-disable-next-line @dbh/redux-extra/prefer-hooks-to-connect
const j=connect(null)(i);return hoistNonReactStatics(j,c)});

/**
 * Custom `react` hook that returns the store injectors.
 * @return {{ injectReducer: Function, injectSaga: Function, ejectSaga: Function }} The store injectors.
 */const useStoreInjectors=()=>{const a=useStore();const c=useMemo(()=>getSagaInjectors(a),[a]),d=useMemo(()=>({...c,injectReducer:a.createReducer}),[c,a]);return d};var useStoreInjectors$1 = useStoreInjectors;

/**
 * @typedef {import('../types').InjectedSagaConfiguration} InjectedSagaConfiguration
 *//**
 * Custom `react` hook that injects a saga.
 * @param {InjectedSagaConfiguration} sagaConfiguration .
 */const useBrowserOnlyInjectSaga=withConformsTo("useBrowserOnlyInjectSaga",[],a=>{useThrowIfPreviousValueDeepButNotReferenceEqual(a);const{injectSaga:b,ejectSaga:c}=useStoreInjectors$1(),{key:d,saga:e,mode:f}=a,g=useSagaInjected(),h=useSagaEjected();useEffect(()=>{const{wasAlreadyInjected:a}=b(d,{saga:e,mode:f});g(d,{wasAlreadyInjected:a});},[b,d,f,e,g]),useEffect(()=>()=>{// Cleanup after the component that uses this hook is unmounted.
c(d),h(d);},[c,d,h]);});var useBrowserOnlyInjectSaga$1 = useBrowserOnlyInjectSaga;

/**
 * @typedef {import('../types').InjectedReducerConfiguration} InjectedReducerConfiguration
 *//**
 * Custom `react` hook that injects a reducer.
 * @param {InjectedReducerConfiguration} reducerConfiguration .
 */const useBrowserOnlyInjectReducer=withConformsTo("useBrowserOnlyInjectReducer",[],a=>{useThrowIfPreviousValueDeepButNotReferenceEqual(a);const{injectReducer:b}=useStoreInjectors$1(),{key:c,reducer:d}=a;useEffect(()=>{b(c,d);},[b,c,d]);});var useBrowserOnlyInjectReducer$1 = useBrowserOnlyInjectReducer;

/**
 * @typedef {import('../types').InjectedSagaConfiguration} InjectedSagaConfiguration
 * @typedef {import('../types').InjectedReducerConfiguration} InjectedReducerConfiguration
 *//**
 * Custom `react` hook that injects a saga and reducer.
 * @param {InjectedSagaConfiguration} sagaConfiguration .
 * @param {InjectedReducerConfiguration} reducerConfiguration .
 */const useBrowserOnlyInjectSagaAndReducer=(a,b)=>{useBrowserOnlyInjectSaga$1(a),useBrowserOnlyInjectReducer$1(b);};var useBrowserOnlyInjectSagaAndReducer$1 = useBrowserOnlyInjectSagaAndReducer;

export { DAEMON, ONCE_TILL_UNMOUNT, REDUCER_EJECTED, REDUCER_INJECTED, RESTART_ON_REMOUNT, SAGA_EJECTED, SAGA_INJECTED, createUseBoundEventCreator$1 as createUseBoundEventCreator, injectReducer, injectSaga, injectSagaAndReducer, reducerInjected, getSagaInjectors as sagaInjectors, useBrowserOnlyInjectReducer$1 as useBrowserOnlyInjectReducer, useBrowserOnlyInjectSaga$1 as useBrowserOnlyInjectSaga, useBrowserOnlyInjectSagaAndReducer$1 as useBrowserOnlyInjectSagaAndReducer };
